import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * EmptyState — centered message for empty lists / blank slates.
 *
 * Use:
 *   <EmptyState
 *     icon={<PackageOpen />}
 *     title="No orders yet"
 *     description="When you submit your first order it'll appear here."
 *     actions={<Button>New order</Button>}
 *   />
 */

export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const sizeConfig = {
  sm: { wrapper: 'py-8 gap-3', title: 'text-base', desc: 'text-sm', icon: 'h-10 w-10' },
  md: { wrapper: 'py-12 gap-4', title: 'text-lg', desc: 'text-sm', icon: 'h-12 w-12' },
  lg: { wrapper: 'py-20 gap-5', title: 'text-2xl', desc: 'text-base', icon: 'h-16 w-16' },
};

export function EmptyState({
  icon,
  title,
  description,
  actions,
  size = 'md',
  className,
  ...props
}: EmptyStateProps) {
  const conf = sizeConfig[size];
  return (
    <div
      className={cn(
        'flex flex-col items-center text-center px-6',
        conf.wrapper,
        className,
      )}
      {...props}
    >
      {icon ? (
        <div
          className={cn(
            'rounded-full bg-sand-100 text-text-muted flex items-center justify-center',
            conf.icon,
          )}
          aria-hidden="true"
        >
          {icon}
        </div>
      ) : null}
      <h3 className={cn('font-semibold text-text-primary', conf.title)}>{title}</h3>
      {description ? (
        <p className={cn('text-text-secondary max-w-md', conf.desc)}>{description}</p>
      ) : null}
      {actions ? <div className="mt-2 flex items-center gap-3">{actions}</div> : null}
    </div>
  );
}
